// ./src/utils/api/EventPartnerService.ts
//
// EventPartner (a confirmed sponsor / partner / exhibitor / vendor on an event)
// service. OpResult-native, event-scoped. A partner links to a catalog package
// (agreed amount defaults from it) and to a company (venue) OR a person; the
// actual money is recorded in the payment ledger (see PartnerPaymentService).
// See em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { listAllPages, ListAllOptions } from "./listAllPages";
import { Pagination, SortItem } from "./commonApiTypes";
import { personAvatarUrl } from "../personAvatar";

// Reuse the shared partner-kind / money-direction vocabulary + display helpers.
export {
  PARTNER_KIND,
  PARTNER_KIND_VALUES,
  PARTNER_KIND_LABELS,
  PARTNER_MONEY_DIRECTION,
  PARTNER_MONEY_DIRECTION_VALUES,
  PARTNER_MONEY_DIRECTION_LABELS,
  partnerKindMeta,
  partnerKindLabel,
  partnerMoneyLabel,
} from "./PartnerPackageService";

// =============================================================================
// GraphQL
// =============================================================================

export const EVENT_PARTNER_FIELDS = `
  id
  accountId
  eventId
  partnerKind
  partnerPackageId
  eventPartnerPackageId
  partnerApplicationId
  venueId
  personId
  orgName
  logoUploadedFileId
  logoUploadedFile { id fileUrl thumbnailUrl }
  websiteUrl
  description
  note
  tierLabel
  contactName
  email
  phone
  moneyDirection
  amount
  currencyCode
  paymentStatus
  utmSource
  utmMedium
  utmCampaign
  utmTerm
  utmContent
  status
  rights
  createdAt
  updatedAt
  venue { id name website logoUploadedFile { id fileUrl thumbnailUrl } }
  person { id firstName lastName email personalWebsite socialUrl1 socialUrl2 socialUrl3 avatarUploadedFile { fileUrl thumbnailUrl } avatarGravatarUrl }
`;

const EVENT_PARTNER_LIST_QUERY = gql`
  query EventPartnerList($filter: EventPartnerFilter, $params: PaginationAndSorting) {
    eventPartnerList(filter: $filter, params: $params) {
      code
      data { ${EVENT_PARTNER_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const EVENT_PARTNER_GET_QUERY = gql`
  query EventPartnerGet($id: ID) {
    eventPartnerGet(id: $id) {
      code
      data { ${EVENT_PARTNER_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const EVENT_PARTNER_CREATE_MUTATION = gql`
  mutation EventPartnerCreate($params: EventPartnerInput) {
    eventPartnerCreate(params: $params) {
      code
      data { ${EVENT_PARTNER_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const EVENT_PARTNER_UPDATE_MUTATION = gql`
  mutation EventPartnerUpdate($where: EventPartnerWhereInput, $params: EventPartnerInput) {
    eventPartnerUpdate(where: $where, params: $params) {
      code
      data { ${EVENT_PARTNER_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const EVENT_PARTNER_REMOVE_MUTATION = gql`
  mutation EventPartnerRemove($where: EventPartnerWhereInput) {
    eventPartnerRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

// ROADMAP #197 — sell a package from the dashboard (one transaction on the server: a place on the offering, the partner,
// the money taken). The payment id comes back for its receipt (`PaymentService.sendReceipt`), sent as its own call.
const EVENT_PARTNER_SELL_MUTATION = gql`
  mutation EventPartnerSell($params: EventPartnerSellInput) {
    eventPartnerSell(params: $params) {
      code
      data {
        paymentId
        partner { ${EVENT_PARTNER_FIELDS} }
      }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types
// =============================================================================

/** #197 — what a package sale sends. The names are the server's field names (its refusals are keyed by them). */
export interface EventPartnerSellInput {
  eventId: string;
  eventPartnerPackageId: string;
  orgName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  websiteUrl?: string | null;
  /** The price agreed; null = the offering's price. */
  amount?: number | null;
  /** The money taken now (0 = nothing yet). */
  receivedAmount?: number | null;
  /** manual (cash) | cheque | pos | other — required when money was taken. */
  paymentMethod?: string | null;
  paidAt?: string | null;
  note?: string | null;
}

/** #197 — what a package sale answers: the partner made, and the payment recorded (null when nothing was taken). */
export interface EventPartnerSale {
  partner?: EventPartner | null;
  paymentId?: string | null;
}

export interface ResolvedFileRef {
  id?: string | null;
  fileUrl?: string | null;
  thumbnailUrl?: string | null;
}

export interface EventPartnerVenueRef {
  id?: string;
  name?: string | null;
  website?: string | null;
  logoUploadedFile?: ResolvedFileRef | null;
}

export interface EventPartnerPersonRef {
  id?: string;
  firstName?: string | null;
  lastName?: string | null;
  /** The linked person's own email — the fallback when the partner has no contact email of its own. */
  email?: string | null;
  personalWebsite?: string | null;
  socialUrl1?: string | null;
  socialUrl2?: string | null;
  socialUrl3?: string | null;
  /** Avatar resolved server-side (uploaded file + Gravatar fallback) — see personAvatarUrl. */
  avatarUploadedFile?: ResolvedFileRef | null;
  avatarGravatarUrl?: string | null;
}

export interface EventPartner {
  id?: string;
  accountId?: string | null;
  eventId?: string | null;
  partnerKind?: string | null;
  partnerPackageId?: string | null;
  /**
   * #197 — the event's offering this partner holds a place on (a SOLD package, or an approved application's). READ-ONLY:
   * the server sets it and refuses it on a save. Removing the partner gives the place back.
   */
  eventPartnerPackageId?: string | null;
  /** The sponsorship application it came from — READ-ONLY. Removing such a partner WITHDRAWS the application. */
  partnerApplicationId?: string | null;
  venueId?: string | null;
  personId?: string | null;
  orgName?: string | null;
  /** The partner's own showcase logo (overrides the linked company logo). */
  logoUploadedFileId?: string | null;
  logoUploadedFile?: ResolvedFileRef | null;
  websiteUrl?: string | null;
  description?: string | null;
  /** Internal note (not shown publicly). */
  note?: string | null;
  tierLabel?: string | null;
  contactName?: string | null;
  email?: string | null;
  phone?: string | null;
  moneyDirection?: string | null;
  /** Agreed amount (defaults from the catalog package). */
  amount?: number | null;
  currencyCode?: string | null;
  paymentStatus?: string | null;
  /** Link-tracking params appended to showcase links. */
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  utmTerm?: string | null;
  utmContent?: string | null;
  status?: number;
  rights?: string[] | null;
  createdAt?: string;
  updatedAt?: string;
  venue?: EventPartnerVenueRef | null;
  person?: EventPartnerPersonRef | null;
}

export interface EventPartnerInput {
  eventId?: string | null;
  partnerKind?: string | null;
  partnerPackageId?: string | null;
  venueId?: string | null;
  personId?: string | null;
  orgName?: string | null;
  logoUploadedFileId?: string | null;
  websiteUrl?: string | null;
  description?: string | null;
  note?: string | null;
  tierLabel?: string | null;
  contactName?: string | null;
  email?: string | null;
  phone?: string | null;
  moneyDirection?: string | null;
  amount?: number | null;
  paymentStatus?: string | null;
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  utmTerm?: string | null;
  utmContent?: string | null;
  status?: number | null;
}

// A bulk update (>1 id) may only mass-change status (server-enforced).
export interface EventPartnerBulkInput {
  status?: number | null;
}

export interface EventPartnerFilter {
  id?: string[] | null;
  eventId?: string[] | null;
  partnerKind?: string[] | null;
  moneyDirection?: string[] | null;
  venueId?: string[] | null;
  personId?: string[] | null;
  status?: number[] | null;
  searchKeyword?: string | null;
}

export interface EventPartnerWhereInput {
  id?: string | string[];
}

/** Display name for a partner. org_name is an OPTIONAL override; when it is blank the
 *  name comes from the linked company (venue), else the linked person. */
export const eventPartnerDisplayName = (p: EventPartner): string => {
  if (p.orgName && p.orgName.trim()) {
    return p.orgName.trim();
  }

  if (p.venue?.name) {
    return p.venue.name;
  }

  if (p.person) {
    const full = [p.person.firstName, p.person.lastName].filter(Boolean).join(" ").trim();

    return full || "—";
  }

  return "—";
};

/** Showcase logo: the partner's own logo, else the linked company's logo, else (for a
 *  person-backed partner) the person's avatar — its uploaded photo or Gravatar fallback,
 *  both resolved server-side (see personAvatarUrl). */
export const eventPartnerLogoUrl = (p: EventPartner): string | null =>
  p.logoUploadedFile?.fileUrl ||
  p.venue?.logoUploadedFile?.fileUrl ||
  (p.person ? personAvatarUrl(p.person) : null) ||
  null;

/** Showcase website: the partner's own snapshot, else the linked company/person site. */
export const eventPartnerWebsite = (p: EventPartner): string | null =>
  p.websiteUrl || p.venue?.website || p.person?.personalWebsite || null;

/** Showcase social links — only a linked person carries socials today. */
export const eventPartnerSocials = (p: EventPartner): string[] =>
  [p.person?.socialUrl1, p.person?.socialUrl2, p.person?.socialUrl3].filter((u): u is string => !!u && !!u.trim());

/** Append the partner's UTM params to a link, preserving any existing query. */
export const appendUtmParams = (url: string | null | undefined, p: EventPartner): string | null => {
  if (!url) {
    return null;
  }

  const params: Array<[string, string | null | undefined]> = [
    ["utm_source", p.utmSource],
    ["utm_medium", p.utmMedium],
    ["utm_campaign", p.utmCampaign],
    ["utm_term", p.utmTerm],
    ["utm_content", p.utmContent],
  ];

  const query = params
    .filter(([, v]) => !!v && !!String(v).trim())
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v).trim())}`)
    .join("&");

  if (!query) {
    return url;
  }

  return url.includes("?") ? `${url}&${query}` : `${url}?${query}`;
};

// =============================================================================
// Service
// =============================================================================

export class EventPartnerService {
  static async list(
    filter?: EventPartnerFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    return ApiService.executeQuery(EVENT_PARTNER_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  /**
   * Every row matching `filter`, paged — for PRINT SHEETS and exports, which need the whole set.
   * ⚠ Never pass a literal `pageSize` in a print page instead of this: paper has no Load-more
   * button, so a capped sheet is indistinguishable from a complete one. See `listAllPages`.
   */
  static async listAll(filter?: EventPartnerFilter, sortBy?: SortItem[], opts?: ListAllOptions): Promise<OpResult> {
    return listAllPages<EventPartner>(EventPartnerService.list, filter, sortBy, opts);
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(EVENT_PARTNER_GET_QUERY, { id });
  }

  static async create(params: EventPartnerInput): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_PARTNER_CREATE_MUTATION, { params });
  }

  static async update(where: EventPartnerWhereInput, params: EventPartnerInput): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_PARTNER_UPDATE_MUTATION, { where, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_PARTNER_REMOVE_MUTATION, { where: { id } });
  }

  static async bulkUpdate(ids: string[], params: EventPartnerBulkInput): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(EVENT_PARTNER_UPDATE_MUTATION, { where: { id: ids }, params });
  }

  /** #197 — sell a package: the buyer becomes a partner on the offering, and the money taken is recorded. */
  static async sell(params: EventPartnerSellInput): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_PARTNER_SELL_MUTATION, { params });
  }

  static async removeMany(ids: string[]): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(EVENT_PARTNER_REMOVE_MUTATION, { where: { id: ids } });
  }
}

/**
 * Where a partner's email goes: its own contact email, else its linked person's. Mirrors the server's
 * `sendPartnerReceipt`, which uses the same order — so the address a screen promises ("Email a receipt
 * to …") is the one the email is actually sent to.
 */
export const partnerEmailOnFile = (partner?: EventPartner | null): string | null =>
  (partner?.email || partner?.person?.email || "").trim() || null;
